import {
  Component,
  ChangeDetectionStrategy,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  BehaviorSubject,
  combineLatest,
  merge,
  Observable,
  of,
  Subject,
  timer,
} from 'rxjs';
import {
  filter,
  map,
  scan,
  switchMap,
  withLatestFrom,
  shareReplay,
  catchError,
  takeWhile,
  take,
} from 'rxjs/operators';
import { shareReplayRefCount, valuesOf } from '@adreno-common';
import {
  ListCampaignSearchCriteriaChildDto,
  SearchCallRecordDto,
  SearchCallRecordsQuery,
} from '../models';
import {
  CancelRecordSearchResult,
  ExistingRecordSearchResult,
  NewRecordSearchResult,
  RecordService,
  WorkService,
} from '../services';
import { SubSink } from 'subsink';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { StartSearchOptions } from '../code';

@Component({
  selector: 'app-record-search',
  templateUrl: './record-search.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecordSearchComponent implements OnInit, OnDestroy {
  @Input()
  public set terms(v: SearchCallRecordsQuery | undefined) {
    this.termsSub.next(v);
  }
  @Input()
  public set options(v: StartSearchOptions | undefined) {
    this.optionsSub.next(v);
  }

  private loadingSub = new BehaviorSubject<boolean | undefined>(undefined);
  private optionsSub = new BehaviorSubject<StartSearchOptions | undefined>(
    undefined
  );
  private results$!: Observable<SearchCallRecordDto[] | undefined>;
  private searchSub = new Subject<void>();
  private subs = new SubSink();
  private termsSub = new BehaviorSubject<SearchCallRecordsQuery | undefined>(
    undefined
  );

  criteria$!: Observable<ListCampaignSearchCriteriaChildDto[]>;

  searchForm!: FormGroup;
  viewModel$!: Observable<ViewModel>;

  constructor(
    private activeModal: NgbActiveModal,
    private recordSvc: RecordService,
    private workSvc: WorkService,
    private fb: FormBuilder
  ) {}

  private initFormGroup() {
    this.searchForm = this.fb.group({
      campaignId: 0,
      company: null,
      first: null,
      last: null,
      address1: null,
      suburb: null,
      postcode: null,
      phone: null,
      u1: null,
      u2: null,
      u3: null,
      u4: null,
      u5: null,
      u6: null,
      u7: null,
      u8: null,
      u9: null,
      u10: null,
      u11: null,
      u12: null,
      u13: null,
      u14: null,
      u15: null,
      u16: null,
      u17: null,
      u18: null,
      u19: null,
      u20: null,
      autoSearch: false,
      autoSelect: false,
    });

    this.subs.sink = this.searchForm
      .get('phone')!
      .valueChanges.pipe(filter((val) => !!val && typeof val === 'string'))
      .subscribe((phone) => {
        const newPhone = phone.replace(/[^0-9*#]/g, '');
        if (newPhone !== phone) {
          this.searchForm.get('phone')!.setValue(newPhone, {
            emitEvent: false,
          });
        }
      });
  }

  private initViewModel() {
    this.viewModel$ = merge<Partial<ViewModel>>(
      this.loadingSub.pipe(map((loading) => ({ loading }))),
      this.loadingSub.pipe(
        filter((loading) => !!loading),
        switchMap(() =>
          timer(0, 1000).pipe(
            map((sec) => 30 - sec),
            takeWhile((sec) => sec >= 0)
          )
        ),
        map((timeout) => ({ timeout }))
      ),
      this.results$.pipe(map((results) => ({ loading: false, results }))),
      this.criteria$.pipe(map((crit) => ({ searchCriteria: crit }))),
      this.optionsSub.pipe(map((options) => ({ options })))
    ).pipe(scan((acc, value) => ({ ...acc, ...value }), {} as ViewModel));
  }

  close() {
    this.activeModal.close({ type: 'cancel' } as CancelRecordSearchResult);
  }

  newRecord() {
    const campaignId = this.searchForm.controls.campaignId.value;
    this.recordSvc.getNewRecord$(campaignId).subscribe((recordId) => {
      this.activeModal.close({
        type: 'new',
        recordId,
      } as NewRecordSearchResult);
    });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  ngOnInit(): void {
    this.criteria$ = this.workSvc.campaignId$.pipe(
      filter((campaignId) => !!campaignId),
      take(1),
      switchMap((campaignId) =>
        this.recordSvc.listCampaignSearchCriteria(campaignId!)
      )
    );

    this.results$ = this.searchSub.pipe(
      switchMap(() => {
        const model = this.searchForm.value as FormModel;
        const query = {
          campaignId: model.campaignId || undefined,
          company: model.company || undefined,
          first: model.first || undefined,
          last: model.last || undefined,
          phone: model.phone || undefined,
          postcode: model.postcode || undefined,
          address1: model.address1 || undefined,
          suburb: model.suburb || undefined,
          u1: model.u1 || undefined,
          u2: model.u2 ? new Date(model.u2) : undefined,
          u3: model.u3 || undefined,
          u4: model.u4 || undefined,
          u5: model.u5 || undefined,
          u6: model.u6 || undefined,
          u7: model.u7 || undefined,
          u8: model.u8 || undefined,
          u9: model.u9 || undefined,
          u10: model.u10 || undefined,
          u11: model.u11 || undefined,
          u12: model.u12 || undefined,
          u13: model.u13 || undefined,
          u14: model.u14 || undefined,
          u15: model.u15 || undefined,
          u16: model.u16 || undefined,
          u17: model.u17 || undefined,
          u18: model.u18 || undefined,
          u19: model.u19 || undefined,
          u20: model.u20 || undefined,
        } as SearchCallRecordsQuery;
        return this.recordSvc
          .searchRecords$(query)
          .pipe(catchError((err) => of(undefined)));
      }),
      shareReplayRefCount()
    );

    this.initViewModel();
    this.initFormGroup();

    this.subs.sink = this.results$.pipe(take(1)).subscribe((results) => {
      const autoSearchCtrl = this.searchForm.get('autoSearch')!;
      const autoSelectCtrl = this.searchForm.get('autoSelect')!;
      if (autoSearchCtrl.value && autoSelectCtrl.value) {
        if (results && results.length === 1) {
          this.selectRecord(results[0].recordId);
        }
      }
      autoSearchCtrl.setValue(false);
      autoSelectCtrl.setValue(false);
    });
    this.subs.sink = this.searchSub.subscribe(() => this.loadingSub.next(true));
    this.subs.sink = combineLatest([this.termsSub, this.optionsSub])
      .pipe(
        filter(([terms, options]) => !!terms && !!options),
        take(1)
      )
      .subscribe(([terms, options]) => {
        this.searchForm.patchValue({ ...terms!, ...options });
        if (options?.autoSearch) {
          this.search();
        }
      });
  }

  search() {
    this.searchSub.next();
  }

  selectRecord(recordId: number) {
    const campaignId = this.searchForm.controls.campaignId.value;
    this.recordSvc.getRecord$(campaignId, recordId).subscribe((record) => {
      this.activeModal.close({
        type: 'existing',
        record,
      } as ExistingRecordSearchResult);
    });
  }
}

interface FormModel {
  address1: string;
  autoSearch: boolean;
  campaignId: number;
  company: string;
  first: string;
  last: string;
  phone: string;
  postcode: string;
  suburb: string;
  u1: string;
  u10: string;
  u11: string;
  u12: string;
  u13: string;
  u14: string;
  u15: string;
  u16: string;
  u17: string;
  u18: string;
  u19: string;
  u2?: string;
  u20: string;
  u3: string;
  u4: string;
  u5: string;
  u6: string;
  u7: string;
  u8: string;
  u9: string;
}

interface ViewModel {
  loading: boolean;
  options?: StartSearchOptions;
  results?: SearchCallRecordDto[];
  searchCriteria?: ListCampaignSearchCriteriaChildDto[];
  timeout?: number;
}
